export const homeMeta = {
  title: "TheCareerSpace | Education Counselling for India & Abroad",
  description:
    "TheCareerSpace helps students and families navigate admissions in India and abroad — bachelor's, master's, MBBS, and international undergraduate programs — with expert counselling and step-by-step support."
};

export const heroContent = {
  eyebrow: "India's Trusted Education Counsellors",
  title: "Your education journey, guided every step of the way.",
  description:
    "Whether you're planning for India or dreaming of studying abroad, TheCareerSpace offers personalised, profile-led counselling for bachelor's, master's, MBBS, and international undergraduate programs — so every decision is made with confidence.",
  primaryCta: {
    label: "Book Free Counselling",
    to: "/contact"
  },
  secondaryCta: {
    label: "Explore Pathways",
    to: "/contact"
  },
  trustLine: "Structured guidance, curated options, and steady support from first call to admission closure.",
  supportPanels: [
    {
      title: "Profile Review",
      text: "We start with academics, goals, entrance context, budget range, and family priorities."
    },
    {
      title: "Focused Shortlisting",
      text: "You receive options that are realistic, relevant, and aligned to eligibility and preferences."
    },
    {
      title: "Application Guidance",
      text: "Documentation, forms, and timelines are handled in sequence to avoid confusion and delays."
    }
  ],
  heroStats: [
    { label: "Pathways", value: "5+" },
    { label: "Counselling Style", value: "1:1" },
    { label: "Study Destinations", value: "9" }
  ],
  heroHighlights: [
    "Step-by-step counselling calls — India & Abroad",
    "College and university-fit focused planning",
    "Parent-inclusive decision support at every stage"
  ]
};

export const trustStripItems = [
  "Expert counselling across all major India admission pathways",
  "9 international study destinations — backed by NextEra Abroad",
  "Parent-friendly communication at every key decision stage",
  "Structured planning around eligibility, budget, and timeline",
  "Guidance from shortlist to final admission confirmation"
];

export const admissionCategories = [
  {
    title: "Undergraduate Abroad",
    audience: "For students dreaming of an international bachelor's degree",
    description:
      "End-to-end support for studying undergraduate programs in USA, UK, Canada, Australia, and 5 more top destinations through our partner NextEra Abroad.",
    highlights: [
      "9 study destinations covered",
      "Scholarship & visa guidance included"
    ],
    ctaLabel: "Explore Study Abroad",
    href: "https://nexteraabroad.com/",
    external: true
  },
  {
    title: "Bachelor's Admissions in India",
    audience: "For Class 12 students choosing undergraduate programs",
    description:
      "Guidance across engineering, management, health sciences, design, and allied undergraduate pathways.",
    highlights: [
      "Eligibility-first shortlisting",
      "Program and campus fit discussions"
    ],
    ctaLabel: "Explore Bachelor's Guidance",
    to: "/bachelors-india"
  },
  {
    title: "Master's Admissions in India",
    audience: "For graduates planning post-graduate progression",
    description:
      "Targeted postgraduate counselling with intake strategy, profile positioning, and practical college selection.",
    highlights: [
      "Career-aligned course planning",
      "Focused shortlist by profile depth"
    ],
    ctaLabel: "Explore Master's Guidance",
    to: "/masters-india"
  },
  {
    title: "MBBS in India",
    audience: "For students pursuing medicine within India",
    description:
      "Guidance on MBBS pathways in India with clear process support for choices, categories, and next steps.",
    highlights: [
      "Structured option comparison",
      "Admission-flow clarity for families"
    ],
    ctaLabel: "Explore MBBS India Guidance",
    to: "/mbbs-india"
  },
  {
    title: "MBBS Abroad",
    audience: "For students evaluating international MBBS pathways",
    description:
      "Practical counselling for MBBS abroad with balanced evaluation of academics, affordability, and readiness.",
    highlights: [
      "Country and college-fit review",
      "Long-term planning conversations"
    ],
    ctaLabel: "Explore MBBS Abroad Guidance",
    to: "/mbbs-abroad"
  }
];

export const whyChooseItems = [
  {
    title: "Real Profile-Based Guidance",
    description:
      "Every recommendation is grounded in your academic profile, goals, and family context — not generic advice that fits no one."
  },
  {
    title: "India & Abroad, All Under One Roof",
    description:
      "From MBBS in India to undergraduate programs in the USA — we counsel across pathways so you never have to start over with someone new."
  },
  {
    title: "Student + Parent Alignment",
    description:
      "We keep student ambitions and parent concerns aligned through structured counselling conversations that respect both perspectives."
  },
  {
    title: "Timeline Discipline",
    description:
      "Applications, documents, and milestones are sequenced clearly to reduce last-minute pressure and missed deadlines."
  },
  {
    title: "Continuity Till Closure",
    description:
      "You work with one aligned team — from first call to final admission confirmation. No hand-offs, no gaps."
  }
];

export const processSteps = [
  {
    step: "01",
    title: "Understand profile and goals",
    description:
      "We map student interests, academics, budget, and family priorities before deciding the direction — India, abroad, or both."
  },
  {
    step: "02",
    title: "Build a shortlist with fit",
    description:
      "A practical shortlist is prepared around eligibility, quality, affordability, and location preferences — local or global."
  },
  {
    step: "03",
    title: "Guide documents and applications",
    description:
      "Our team helps with paperwork readiness, application sequencing, and submission checkpoints across all pathways."
  },
  {
    step: "04",
    title: "Support final decision and closure",
    description:
      "We guide offer evaluation and next steps so admission completion remains smooth, informed, and stress-free."
  }
];

export const reassuranceContent = {
  badge: "Student + Parent Reassurance",
  title: "A counselling experience that supports both students and families",
  description:
    "Admissions — whether in India or abroad — can feel overwhelming when options are many and timelines are tight. TheCareerSpace keeps discussions calm, practical, and decision-focused.",
  studentCard: {
    title: "For Students",
    intro: "If you are unsure where to begin, we help you move step by step.",
    points: [
      "Get clarity on suitable pathways — India or abroad",
      "Compare options without information overload",
      "Understand what to do next after each counselling call"
    ]
  },
  parentCard: {
    title: "For Parents",
    intro: "If you need confidence before major decisions, we keep the process transparent.",
    points: [
      "Clear discussions on options, affordability, and trade-offs",
      "Structured updates through critical milestones",
      "Practical support for responsible decision-making"
    ]
  },
  bridgeNotes: [
    "Joint counselling conversations when required",
    "Aligned action plan for student and parent",
    "Consistent guidance until admission closure"
  ]
};

export const statsContent = {
  badge: "Guidance Framework",
  title: "Built around a structured counselling model",
  description:
    "These numbers reflect the TheCareerSpace counselling and college coverage framework — spanning India pathways and international study destinations.",
  cards: [
    {
      value: 5,
      suffix: "+",
      label: "Admission Pathways",
      detail: "Bachelor's, Master's, MBBS India, MBBS Abroad & UG Abroad"
    },
    {
      value: 9,
      suffix: "",
      label: "Study Destinations",
      detail: "USA, UK, Canada, Australia, Dubai, Singapore & more"
    },
    {
      value: 45,
      suffix: "+",
      label: "College Profiles",
      detail: "Listed for progressive guidance expansion"
    },
    {
      value: 4,
      suffix: "",
      label: "Core Process Stages",
      detail: "From profile review to admission closure"
    }
  ]
};
